import React, { useState, useRef, useCallback } from 'react';
import { useThemeMode } from '../../theme/ThemeContext';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  Tooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const shortcuts = [
  { keys: 'Green "+" button', description: 'Create a new task' },
  { keys: 'Top filter bar', description: 'Switch between All / Mine / Sent / Projects / Team' },
  { keys: 'Sidebar icons', description: 'Navigate to Profile, Projects, Settings' },
  { keys: 'Search bar', description: 'Search tasks by title' },
  { keys: 'Task card click', description: 'Open task details and edit' },
];

const keyboardShortcuts = [
  { keys: '⌘K / Ctrl+K', description: 'Open command palette (search + quick actions)' },
  { keys: 'N', description: 'Create a new task' },
  { keys: '/', description: 'Focus the search bar' },
  { keys: '1 / 2 / 3 / 4', description: 'Scroll to To Do / Planned / Doing / Done' },
  { keys: '?', description: 'Show all keyboard shortcuts' },
  { keys: 'Esc', description: 'Close any open dialog' },
];

const HelpButton = ({ onOpenGuide, variant = 'default' }) => {
  const { themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';
  const isFooter = variant === 'footer';
  const [anchorEl, setAnchorEl] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const open = Boolean(anchorEl);
  const pendingAction = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setShowShortcuts(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowShortcuts(false);
  };

  // Run pending action after the Menu exit transition completes
  const handleMenuExited = useCallback(() => {
    if (pendingAction.current) {
      pendingAction.current();
      pendingAction.current = null;
    }
  }, []);

  const handleOpenGuide = () => {
    pendingAction.current = () => { if (onOpenGuide) onOpenGuide(); };
    handleClose();
  };

  const handleToggleShortcuts = () => {
    setShowShortcuts((prev) => !prev);
  };

  return (
    <>
      <Tooltip title="Help & shortcuts" arrow>
      <Box
        onClick={handleClick}
        sx={{
          width: 46,
          height: 46,
          borderRadius: '50%',
          backgroundColor: isFooter ? '#ffffff' : (isDark ? '#0e6c52' : '#27ae86'),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          color: isFooter ? (isDark ? '#0e6c52' : '#27ae86') : '#fff',
          boxShadow: isFooter ? '0 2px 8px rgba(0,0,0,0.2)' : (isDark ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(39, 174, 134, 0.35)'),
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: isFooter ? '#f0f0f0' : (isDark ? '#15806a' : '#1f8f6d'),
            transform: 'scale(1.1)',
            boxShadow: isFooter ? '0 4px 12px rgba(0,0,0,0.25)' : (isDark ? '0 6px 14px rgba(0, 0, 0, 0.5)' : '0 6px 14px rgba(31, 143, 109, 0.45)'),
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 26 }} />
      </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionProps={{ onExited: handleMenuExited }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 240,
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            mt: -1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>
            Help & Resources
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={handleOpenGuide}>
          <ListItemIcon>
            <PlayCircleOutlineIcon sx={{ color: '#27ae86' }} />
          </ListItemIcon>
          <ListItemText
            primary="Restart Tour"
            secondary="Replay the welcome guide"
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>

<MenuItem onClick={handleToggleShortcuts}>
          <ListItemIcon>
            <KeyboardOutlinedIcon sx={{ color: '#27ae86' }} />
          </ListItemIcon>
          <ListItemText
            primary="Quick Reference"
            secondary="Common actions overview"
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>

        {showShortcuts && (
          <Box sx={{ px: 2, py: 1, backgroundColor: '#f8fdf9' }}>
            {shortcuts.map((s, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: '1px solid #e8f5e9',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#27ae86',
                    fontSize: '0.78rem',
                    mr: 2,
                  }}
                >
                  {s.keys}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#666', fontSize: '0.75rem', textAlign: 'right' }}
                >
                  {s.description}
                </Typography>
              </Box>
            ))}

            {/* Keyboard shortcuts section */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                color: '#1f8f6d',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 1.5,
                mb: 0.5,
              }}
            >
              Keyboard Shortcuts
            </Typography>
            {keyboardShortcuts.map((s, i) => (
              <Box
                key={`kb-${i}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: i < keyboardShortcuts.length - 1 ? '1px solid #e8f5e9' : 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#27ae86',
                    fontSize: '0.72rem',
                    fontFamily: 'monospace',
                    backgroundColor: '#e8f5e9',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    mr: 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.keys}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#666', fontSize: '0.75rem', textAlign: 'right' }}
                >
                  {s.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider />
        <MenuItem
          onClick={() => {
            pendingAction.current = () => {
              window.dispatchEvent(new CustomEvent('openFeedbackDialog'));
            };
            handleClose();
          }}
        >
          <ListItemIcon>
            <InfoOutlinedIcon sx={{ color: '#27ae86' }} />
          </ListItemIcon>
          <ListItemText
            primary="Send Feedback"
            secondary="Report bugs or suggest features"
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default HelpButton;
